import React from 'react';
import { 
  Box, 
  Typography, 
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Link,
  IconButton,
  Tooltip,
  Collapse
} from '@mui/material';
import { 
  Description as DocIcon,
  Link as LinkIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon
} from '@mui/icons-material';
import { PolicyReferenceView } from '../../../services/AppealsService';

interface PolicyReferencesPanelProps {
  references: PolicyReferenceView[];
}

const PolicyReferencesPanel: React.FC<PolicyReferencesPanelProps> = ({ references }) => {
  const [expanded, setExpanded] = React.useState<string | null>(null);

  if (!references || references.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="body2" color="textSecondary">
          No policy references attached to this appeal.
        </Typography>
      </Box>
    );
  }

  return (
    <List dense sx={{ py: 0 }}>
      {references.map((ref) => (
        <React.Fragment key={ref.id}>
          <ListItem
            sx={{
              borderBottom: '1px solid #e2e8f0',
              '&:last-child': { borderBottom: 'none' },
              flexDirection: 'column',
              alignItems: 'flex-start',
              py: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {ref.isUserUploaded ? (
                  <UploadIcon fontSize="small" sx={{ color: '#3b82f6' }} />
                ) : (
                  <SearchIcon fontSize="small" sx={{ color: '#64748b' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {ref.policyName}
                    </Typography>
                    <Chip
                      label={ref.payerName}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="textSecondary">
                    {ref.isUserUploaded ? 'Uploaded document' : 'Web search result'} • 
                    Confidence: {Math.round(ref.confidenceScore * 100)}%
                  </Typography>
                }
              />
              <IconButton 
                size="small" 
                onClick={() => setExpanded(expanded === ref.id ? null : ref.id)}
              >
                {expanded === ref.id ? <CollapseIcon /> : <ExpandIcon />}
              </IconButton>
            </Box>
            
            <Collapse in={expanded === ref.id} sx={{ width: '100%', pl: 4 }}>
              <Box sx={{ 
                backgroundColor: '#f8fafc', 
                p: 1.5, 
                borderRadius: 1, 
                mt: 1 
              }}>
                {ref.relevantExcerpt && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                      Relevant Excerpt:
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontStyle: 'italic',
                      color: '#475569',
                      mt: 0.5,
                      whiteSpace: 'pre-wrap'
                    }}>
                      "{ref.relevantExcerpt}"
                    </Typography>
                  </Box>
                )}
                
                {ref.sourceUrl && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    <LinkIcon fontSize="small" sx={{ color: '#3b82f6' }} />
                    <Link 
                      href={ref.sourceUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      variant="caption"
                      sx={{ color: '#3b82f6' }}
                    >
                      View Source
                    </Link>
                  </Box>
                )}
                
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                  Retrieved: {new Date(ref.retrievalDate).toLocaleDateString()} • 
                  Query: "{ref.searchQuery}"
                </Typography>
              </Box>
            </Collapse>
          </ListItem>
        </React.Fragment>
      ))}
    </List>
  );
};

export default PolicyReferencesPanel;
